// Company / school logo lookup by name.
//
// A CV entry stores only a typed name ("Улаанбаатар синема ХХК", "Шихихутуг их сургууль")
// — there is no logo field on work experience or education — so the mark has to be
// resolved from the name. This is that resolver.
//
// The table behind it is cv_companies / cv_universities, the same corpora the CV
// autocomplete searches; scripts/build-logo-directory.js writes the logo and the match key
// onto those rows at seed time. This file used to ship a sharded copy of that table under
// public/data/logos (1.3 MB across 64 files); it asks the server now, which means one
// source of truth instead of a generated duplicate that had to be rebuilt and redeployed
// whenever the corpus changed.

import { registeredLogos, searchLogos } from "../api/services/cvReference";

// Keep in sync with normalizeName() in scripts/build-logo-directory.js — a typed name only
// finds its row if it normalizes to exactly the key that script wrote. These two are the
// only implementations; nothing server-side normalizes anything.
//
// Latin letters that are visually identical to Cyrillic ones, folded to the Cyrillic side.
// Mongolian company names are full of them — "Улаанбаатар синема XXК" spells its ХХК with
// two Latin X's — and without this a name someone types with the Cyrillic keys never
// matches the row stored with the Latin ones. Folding is applied to the index and to the
// query alike, so the two always agree; an all-Latin English name folds into
// Cyrillic-looking nonsense, which matches its own folded key just the same.
const HOMOGLYPHS = {
  A: "А", B: "В", C: "С", E: "Е", H: "Н", K: "К", M: "М",
  O: "О", P: "Р", T: "Т", X: "Х", Y: "У",
  a: "а", b: "в", c: "с", e: "е", h: "н", k: "к", m: "м",
  o: "о", p: "р", t: "т", x: "х", y: "у",
};

// Both cases of the same twelve letters, which is what makes the key independent of how
// a name was capitalized. Folding runs before lowercasing, so covering only the capitals
// (as this did) gave "ABC" the key "авс" and "abc" the key "аbс" — the same name, two
// keys, and 588 rows of the corpus were unreachable from one spelling or the other.
// Lowercase b/h/m/t do not resemble в/н/м/т the way their capitals do, but they are
// folded anyway: consistency between the two sides is what matching needs, not
// resemblance.
const foldHomoglyphs = (s) => s.replace(/[ABCEHKMOPTXYabcehkmoptxy]/g, (ch) => HOMOGLYPHS[ch] || ch);

// The legal form is noise for matching: the same employer is written "Болор ХХК" on one
// CV and "Болор" on the next. Dropped as whole words from both sides.
//
// Matched by splitting on spaces rather than with \b, which is ASCII-only in JavaScript
// and therefore never fires around Cyrillic — a \bххк\b pattern silently matches
// nothing at all. The tokens are folded and lowercased like the text they run against.
const LEGAL_TOKENS = new Set(
  ["ххк", "хк", "ххн", "хзх", "ттк", "нэгдэл", "llc", "ltd", "inc", "jsc", "corp"]
    .map((w) => foldHomoglyphs(w).toLowerCase())
);

export const normalizeName = (raw) =>
  foldHomoglyphs(String(raw || ""))
    .toLowerCase()
    .replace(/[«»"“”„'‘’`]/g, " ")
    .replace(/[.,\-–—_/\\()[\]{}!?:;+*&]/g, " ")
    .split(/\s+/)
    .filter((w) => w && !LEGAL_TOKENS.has(w))
    .join(" ");

/**
 * Companies registered on this platform, keyed the same way everything else here is.
 *
 * Checked before the reference directory, and that order is the point: a registered
 * company's own uploaded logo is the right mark to show, and the directory frequently does
 * not have the company at all — OnePlaceHR is in neither zangia's list nor cv_companies,
 * while its real logo has been sitting on its own company row all along.
 *
 * Loaded once per session (~190 rows) and normalized here, because normalizeName lives in
 * this file and nowhere else.
 */
let registered = null;
let registeredPending = null;

const loadRegistered = () => {
  if (registered) return Promise.resolve(registered);
  if (registeredPending) return registeredPending;
  registeredPending = registeredLogos()
    .then((rows) => {
      const map = new Map();
      (rows || []).forEach((r) => {
        const key = normalizeName(r.name);
        // First row wins: the same company is registered more than once (five rows for
        // OnePlaceHR alone), and the earliest is the one with the longest history.
        if (key.length >= 2 && r.logoUrl && !map.has(key)) map.set(key, r.logoUrl);
      });
      // A failed request caches an empty map rather than retrying on every lookup; the
      // directory below still answers, so the page degrades to what it showed before.
      registered = map;
      registeredPending = null;
      return map;
    })
    .catch(() => {
      registered = new Map();
      registeredPending = null;
      return registered;
    });
  return registeredPending;
};

/** key -> logo URL, or null once the server has said it has none. Lives for the session. */
const resolved = new Map();
/** key -> the in-flight promise that will settle it, so a key is never asked for twice. */
const inFlight = new Map();

// Keys waiting for the next flush. A profile mounts five experience rows and two schools
// in the same tick; batching them means one request instead of seven, without any caller
// having to know the others exist.
let queue = new Set();
let queued = null;

const flush = async () => {
  const keys = [...queue];
  queue = new Set();
  queued = null;
  if (keys.length === 0) return;

  // Registered companies first — their own logo beats a directory guess, and it is the
  // only one that exists for a company the directory never listed.
  const own = await loadRegistered();
  const fromOwn = keys.filter((k) => own.has(k));
  fromOwn.forEach((k) => resolved.set(k, own.get(k)));

  const remaining = keys.filter((k) => !own.has(k));
  if (remaining.length === 0) {
    keys.forEach((k) => inFlight.delete(k));
    return;
  }

  const rows = await searchLogos(remaining);
  // A failed request must not be cached as "no logo" — leaving these unresolved lets the
  // next mount try again, where a null would have been permanent for the session.
  if (rows) {
    const found = new Map(rows.map((r) => [r.key, r.logoUrl]));
    remaining.forEach((k) => resolved.set(k, found.get(k) || null));
  }
  keys.forEach((k) => inFlight.delete(k));
};

const request = (key) => {
  if (inFlight.has(key)) return inFlight.get(key);

  queue.add(key);
  // A microtask, not a timer: everything mounting in the same render lands in one batch,
  // and nothing waits a frame it did not have to.
  if (!queued) queued = Promise.resolve().then(flush);

  const p = queued.then(() => (resolved.has(key) ? resolved.get(key) : null));
  inFlight.set(key, p);
  return p;
};

/**
 * Full logo URL for a company or school name, or null when the name isn't in the
 * directory (which is the common case for small employers — callers must handle it).
 *
 * @param {string} name as typed on the CV
 * @returns {Promise<string|null>}
 */
export const lookupLogo = async (name) => {
  const key = normalizeName(name);
  if (key.length < 2) return null;
  if (resolved.has(key)) return resolved.get(key);
  return request(key);
};

/**
 * Same lookup without the wait, for a name already resolved this session. Returns
 * undefined when it has not been asked for yet (distinct from null = "asked, no logo"),
 * so a caller can tell "not known yet" from "known to have none".
 */
export const peekLogo = (name) => {
  const key = normalizeName(name);
  if (key.length < 2) return null;
  return resolved.has(key) ? resolved.get(key) : undefined;
};
